import { useForm } from 'react-hook-form'
import { joiResolver } from '@hookform/resolvers/joi'
import { TextField } from 'uikit'
import styles from './styles.module.scss'
import { Button, Icon, Loader, PasswordGuidelines, Text } from 'components'
import { usePasswordGuidelines, useTenant, useToast } from 'shared/hooks'
import {
  useChangePassword,
  usePostResetPassword,
  useValidatePasswordToken,
} from 'services/auth'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { schema } from './schema'
import { getFeedbackContent, getView } from './utils'
import { Feedback } from './types'

type Password = {
  password: string
  confirmation: string
}

const SetPassword = () => {
  const {
    register,
    handleSubmit,
    watch,
    formState: { isValid, isDirty, errors },
  } = useForm<Password>({
    mode: 'onChange',
    resolver: joiResolver(schema),
    defaultValues: {
      password: '',
      confirmation: '',
    },
  })

  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { tenant } = useTenant()
  const { addToast } = useToast()

  const token = searchParams.get('token')
  const email = searchParams.get('email') || ''
  const password = watch('password')
  const confirmation = watch('confirmation')

  const [feedback, setFeedback] = useState<Feedback | undefined>()
  const [countdown, setCountdown] = useState(0)

  const isCountingDown = feedback === 'emailError' && countdown > 0
  const feedbackContent = getFeedbackContent(email, feedback)

  const { rules, allValid } = usePasswordGuidelines(
    password,
    confirmation,
    isDirty,
  )

  const { mutate: setPassword, isLoading } = useChangePassword()

  const { mutate: validateToken, isLoading: isValidatingToken } =
    useValidatePasswordToken()

  const { mutate: resetPassword, isLoading: isSendingNewLink } =
    usePostResetPassword()

  const sendNewLink = () => {
    resetPassword(
      {
        email,
      },
      {
        onError() {
          setCountdown(30)
          setFeedback('emailError')
        },
        onSuccess() {
          setFeedback('emailSent')
        },
      },
    )
  }

  useEffect(() => {
    if (!token) {
      navigate('/login')
      return
    }

    validateToken(token, {
      onError() {
        setFeedback('tokenExpired')
      },
    })
  }, [])

  useEffect(() => {
    if (isCountingDown) {
      const timer = setTimeout(
        () => setCountdown((counter) => counter - 1),
        1000,
      )
      return () => clearTimeout(timer)
    }
  }, [feedback, countdown])

  const onSubmit = (data: Password) => {
    setPassword(
      {
        accessToken: token || '',
        newPassword: data.password,
      },
      {
        onError: () => {
          addToast({
            message: 'Erro ao salvar senha. Tente novamente.',
            type: 'alert',
          })
        },
        onSuccess() {
          setFeedback('success')
        },
      },
    )
  }

  return {
    loading: <Loader />,
    feedback: (
      <div className={styles.feedback}>
        <Icon
          className={styles.logo}
          height={42}
          width={186}
          url={tenant?.brand.images.horizontalLogo}
        />

        <Text size="2xl" weight="bold" as="h1">
          {feedbackContent.title}
        </Text>

        <Text size="md" as="p">
          {feedbackContent.description}
        </Text>

        {feedbackContent.action && (
          <Button
            onClick={sendNewLink}
            isLoading={isSendingNewLink}
            disabled={isCountingDown}
            type="button"
            width={312}
          >
            {feedbackContent.action}
            {isCountingDown && ` (${countdown}s)`}
          </Button>
        )}
      </div>
    ),
    form: (
      <div className={styles.form}>
        <header>
          <Icon
            className={styles.logo}
            height={42}
            width={186}
            url={tenant?.brand.images.horizontalLogo}
          />

          <div>
            <Text weight="bold" as="h1" size="2xl">
              Crie sua senha de acesso
            </Text>
            <Text size="md" as="p">
              Digite sua senha de acesso e confirme para concluir o cadastro.
            </Text>
          </div>
        </header>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div>
            <TextField
              label="Nova senha"
              type="password"
              {...register('password')}
            />
            <TextField
              label="Confirmação de senha"
              type="password"
              {...register('confirmation')}
            />

            <PasswordGuidelines rules={rules} />
          </div>

          <Button
            width={400}
            type="submit"
            disabled={!isValid || !allValid}
            isLoading={isLoading}
          >
            Salvar nova senha
          </Button>
        </form>
      </div>
    ),
  }[getView(isValidatingToken, !!feedback)]
}

export default SetPassword
